/**
 * Utilitaires avancés pour le plugin Calendrier
 */

import { CalendarEvent, CalendarConfig } from '../plugins/config/CalendarConfig'; // ✅ CHEMIN CORRIGÉ

// Types étendus pour les analytics
export interface CalendarAnalytics {
  totalEvents: number;
  eventsByCategory: Record<string, number>;
  eventsByMonth: Record<string, number>;
  upcomingEvents: number;
  pastEvents: number;
  recurringEvents: number;
  allDayEvents: number;
  highPriorityEvents: number;
  eventsWithLocation: number;
  averageEventsPerWeek: number;
  busyDays: string[];
  categoryDistribution: { category: string; count: number; percentage: number }[];
  monthlyTrends: { month: string; count: number }[];
  weeklyDistribution: { day: string; count: number }[];
  priorityDistribution: { priority: string; count: number; percentage: number }[];
}

// Types pour l'export
export interface ExportOptions {
  format: 'csv' | 'excel' | 'ical' | 'json';
  includeDescription: boolean;
  includeLocation: boolean;
  includeAttendees: boolean;
  dateRange?: { start: string; end: string };
  categories?: string[];
  filename?: string;
}

// Types pour les récurrences avancées
export interface RecurrenceRule {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number; // Tous les N jours/semaines/mois/années
  endDate?: string;
  count?: number; // Nombre d'occurrences
  byWeekDay?: number[]; // Jours de la semaine (0=dimanche)
  byMonthDay?: number[]; // Jours du mois
  byMonth?: number[]; // Mois (1-12)
  exceptions?: string[]; // Dates d'exception
}

// Types pour les rappels/notifications
export interface EventReminder {
  id: string;
  eventId: string;
  type: 'popup' | 'email' | 'browser';
  triggerTime: number; // Minutes avant l'événement
  message?: string;
  sent: boolean;
  scheduledTime: Date;
}

// Validation avancée des événements
export class CalendarValidator {
  static validateEvent(event: Partial<CalendarEvent>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    // Validation du titre
    if (!event.title || event.title.trim().length === 0) {
      errors.push('Le titre est obligatoire');
    } else if (event.title.length > 100) {
      errors.push('Le titre ne doit pas dépasser 100 caractères');
    }
    
    // Validation de la date
    if (!event.date) {
      errors.push('La date est obligatoire');
    } else {
      const eventDate = new Date(event.date);
      const eventEndDate = event.endDate ? new Date(event.endDate) : eventDate;
      if (isNaN(eventDate.getTime())) {
        errors.push('La date n\'est pas valide');
      } else if (eventDate < new Date('1900-01-01')) {
        errors.push('La date ne peut pas être antérieure à 1900');
      } else if (eventDate > new Date('2099-12-31')) {
        errors.push('La date ne peut pas être postérieure à 2099');
      }
    }

    // Validation de la date de fin (si fournie)
    if (event.endDate) {
      const endDate = new Date(event.endDate);
      if (isNaN(endDate.getTime())) {
        errors.push('La date de fin n\'est pas valide');
      } else {
        if (endDate < new Date('1900-01-01')) {
          errors.push('La date de fin ne peut pas être antérieure à 1900');
        } else if (endDate > new Date('2099-12-31')) {
          errors.push('La date de fin ne peut pas être postérieure à 2099');
        }
        if (event.date) {
          const startDate = new Date(event.date);
          if (!isNaN(startDate.getTime()) && endDate < startDate) {
            errors.push('La date de fin doit être postérieure ou égale à la date de début');
          }
        }
      }
    }
    
    // Validation de l'heure
    if (event.time && !event.allDay) {
      const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(event.time)) {
        errors.push('L\'heure n\'est pas au format valide (HH:MM)');
      }
      
      if (event.endTime) {
        if (!timeRegex.test(event.endTime)) {
          errors.push('L\'heure de fin n\'est pas au format valide (HH:MM)');
        } else {
          const startTime = new Date(`2000-01-01 ${event.time}`);
          const endTime = new Date(`2000-01-01 ${event.endTime}`);
          if (endTime <= startTime) {
            errors.push('L\'heure de fin doit être postérieure à l\'heure de début');
          }
        }
      }
    }
    
    // Validation de la description
    if (event.description && event.description.length > 1000) {
      errors.push('La description ne doit pas dépasser 1000 caractères');
    }
    
    // Validation de la localisation
    if (event.location && event.location.length > 200) {
      errors.push('La localisation ne doit pas dépasser 200 caractères');
    }
    
    // Validation de la couleur
    if (event.color) {
      const colorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
      if (!colorRegex.test(event.color)) {
        errors.push('La couleur doit être au format hexadécimal (#RRGGBB)');
      }
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }
  
  static validateConfig(config: Partial<CalendarConfig>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!config.title || config.title.trim().length === 0) {
      errors.push('Le titre du calendrier est obligatoire');
    }
    
    if (config.calendarHeight && (config.calendarHeight < 200 || config.calendarHeight > 2000)) {
      errors.push('La hauteur doit être comprise entre 200 et 2000 pixels');
    }
    
    if (config.events) {
      config.events.forEach((event, index) => {
        const eventValidation = this.validateEvent(event);
        if (!eventValidation.valid) {
          errors.push(`Événement ${index + 1}: ${eventValidation.errors.join(', ')}`);
        }
      });
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }
}

// Analytics pour le calendrier
export class CalendarAnalytics {
  static generateAnalytics(events: CalendarEvent[]): CalendarAnalytics {
    const now = new Date();
    const analytics: CalendarAnalytics = {
      totalEvents: events.length,
      eventsByCategory: {},
      eventsByMonth: {},
      upcomingEvents: 0,
      pastEvents: 0,
      recurringEvents: 0,
      allDayEvents: 0,
      highPriorityEvents: 0,
      eventsWithLocation: 0,
      averageEventsPerWeek: 0,
      busyDays: [],
      categoryDistribution: [],
      monthlyTrends: [],
      weeklyDistribution: [],
      priorityDistribution: []
    };
    
    // Compteurs de base
    events.forEach(event => {
      const eventDate = new Date(event.date);
      const eventEndDate = event.endDate ? new Date(event.endDate) : eventDate;
      
      // Catégories
      analytics.eventsByCategory[event.category] = (analytics.eventsByCategory[event.category] || 0) + 1;
      
      // Mois
      const monthKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;
      analytics.eventsByMonth[monthKey] = (analytics.eventsByMonth[monthKey] || 0) + 1;
      
      // Événements à venir vs passés
      if (eventEndDate >= now) {
        analytics.upcomingEvents++;
      } else {
        analytics.pastEvents++;
      }
      
      // Récurrents
      if (event.recurrence && event.recurrence !== 'none') {
        analytics.recurringEvents++;
      }
      
      // Toute la journée
      if (event.allDay) {
        analytics.allDayEvents++;
      }
      
      // Priorité élevée
      if (event.priority === 'high') {
        analytics.highPriorityEvents++;
      }
      
      // Avec localisation
      if (event.location && event.location.trim()) {
        analytics.eventsWithLocation++;
      }
    });
    
    // Distribution des catégories
    analytics.categoryDistribution = Object.entries(analytics.eventsByCategory)
      .map(([category, count]) => ({
        category,
        count,
        percentage: Math.round((count / analytics.totalEvents) * 100)
      }))
      .sort((a, b) => b.count - a.count);
    
    // Tendances mensuelles
    analytics.monthlyTrends = Object.entries(analytics.eventsByMonth)
      .map(([month, count]) => ({ month, count }))
      .sort((a, b) => a.month.localeCompare(b.month));
    
    // Distribution hebdomadaire
    const weekDays = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const weeklyCount = Array(7).fill(0);
    
    events.forEach(event => {
      const eventDate = new Date(event.date);
      weeklyCount[eventDate.getDay()]++;
    });
    
    analytics.weeklyDistribution = weekDays.map((day, index) => ({
      day,
      count: weeklyCount[index]
    }));
    
    // Distribution des priorités
    const priorityCount = { low: 0, normal: 0, high: 0 };
    events.forEach(event => {
      priorityCount[event.priority || 'normal']++;
    });
    
    analytics.priorityDistribution = Object.entries(priorityCount)
      .map(([priority, count]) => ({
        priority,
        count,
        percentage: analytics.totalEvents > 0 ? Math.round((count / analytics.totalEvents) * 100) : 0
      }));
    
    // Jours chargés (plus de 3 événements)
    const dayEventCount: Record<string, number> = {};
    events.forEach(event => {
      dayEventCount[event.date] = (dayEventCount[event.date] || 0) + 1;
    });
    
    analytics.busyDays = Object.entries(dayEventCount)
      .filter(([_, count]) => count >= 3)
      .map(([date]) => date)
      .sort();
    
    // Moyenne d'événements par semaine
    if (analytics.totalEvents > 0) {
      const firstEvent = new Date(Math.min(...events.map(e => new Date(e.date).getTime())));
      const lastEvent = new Date(Math.max(...events.map(e => new Date(e.date).getTime())));
      const weeksDiff = Math.max(1, Math.ceil((lastEvent.getTime() - firstEvent.getTime()) / (7 * 24 * 60 * 60 * 1000)));
      analytics.averageEventsPerWeek = Math.round((analytics.totalEvents / weeksDiff) * 10) / 10;
    }
    
    return analytics;
  }
}

// Export des données
export class CalendarExporter {
  static async exportToCSV(events: CalendarEvent[], options: ExportOptions = { format: 'csv', includeDescription: true, includeLocation: true, includeAttendees: true }): Promise<string> {
    const headers = [
      'Titre',
      'Date',
      'Date de fin',
      'Heure',
      'Heure de fin',
      'Catégorie',
      'Priorité',
      'Toute la journée'
    ];
    
    if (options.includeDescription) headers.push('Description');
    if (options.includeLocation) headers.push('Localisation');
    if (options.includeAttendees) headers.push('Participants');
    
    headers.push('Couleur', 'Récurrence');
    
    const csvContent = [
      headers.join(','),
      ...events
        .filter(event => {
          if (options.dateRange) {
            const eventStart = new Date(event.date);
            const eventEnd = event.endDate ? new Date(event.endDate) : eventStart;
            const startDate = new Date(options.dateRange.start);
            const endDate = new Date(options.dateRange.end);
            return eventEnd >= startDate && eventStart <= endDate;
          }
          if (options.categories && options.categories.length > 0) {
            return options.categories.includes(event.category);
          }
          return true;
        })
        .map(event => {
          const row = [
            `"${event.title.replace(/"/g, '""')}"`,
            event.date,
            event.endDate || '',
            event.time || '',
            event.endTime || '',
            event.category,
            event.priority || 'normal',
            event.allDay ? 'Oui' : 'Non'
          ];
          
          if (options.includeDescription) {
            row.push(`"${(event.description || '').replace(/"/g, '""')}"`);
          }
          if (options.includeLocation) {
            row.push(`"${(event.location || '').replace(/"/g, '""')}"`);
          }
          if (options.includeAttendees) {
            row.push(`"${(event.attendees || []).join('; ')}"`);
          }
          
          row.push(event.color || '', event.recurrence || 'none');
          
          return row.join(',');
        })
    ].join('\n');
    
    return csvContent;
  }
  
  static async exportToICS(events: CalendarEvent[], calendarName: string = 'Mon Calendrier'): Promise<string> {
    const formatDate = (dateStr: string, timeStr?: string): string => {
      const date = new Date(dateStr);
      if (timeStr && !timeStr.match(/^\d{2}:\d{2}$/)) {
        return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      }
      
      if (timeStr) {
        const [hours, minutes] = timeStr.split(':');
        date.setHours(parseInt(hours), parseInt(minutes));
        return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      }
      
      // Événement toute la journée
      return date.toISOString().split('T')[0].replace(/-/g, '');
    };
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Book Editor//Calendar Plugin//FR',
      `X-WR-CALNAME:${calendarName}`,
      'X-WR-TIMEZONE:Europe/Paris',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
    ];
    
    // Ajouter les événements
    events.forEach(event => {
      const effectiveEndDate = event.endDate && event.endDate >= event.date ? event.endDate : event.date;
      const hasRange = Boolean(event.endDate && event.endDate >= event.date);
      const eventLines = [
        'BEGIN:VEVENT',
        `UID:${event.id}@bookeditor.local`,
        `DTSTART${event.allDay ? ';VALUE=DATE' : ''}:${formatDate(event.date, event.time)}`,
        event.endTime && !event.allDay ? `DTEND:${formatDate(effectiveEndDate, event.endTime)}` : 
          (event.allDay || hasRange) ? `DTEND;VALUE=DATE:${formatDate(effectiveEndDate)}` : '',
        `SUMMARY:${event.title}`,
        event.description ? `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}` : '',
        event.location ? `LOCATION:${event.location}` : '',
        `CATEGORIES:${event.category.toUpperCase()}`,
        `PRIORITY:${event.priority === 'high' ? '1' : event.priority === 'low' ? '9' : '5'}`,
        'STATUS:CONFIRMED',
        'TRANSP:OPAQUE',
        `CREATED:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
        `LAST-MODIFIED:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
        'END:VEVENT'
      ].filter(line => line.length > 0);
      
      icsContent.push(...eventLines);
    });
    
    icsContent.push('END:VCALENDAR');
    
    return icsContent.join('\n');
  }
  
  static downloadFile(content: string, filename: string, mimeType: string) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// Gestion des récurrences
export class RecurrenceManager {
  static generateRecurringEvents(baseEvent: CalendarEvent, rule: RecurrenceRule, maxEvents: number = 100): CalendarEvent[] {
    const events: CalendarEvent[] = [];
    const startDate = new Date(baseEvent.date);
    let currentDate = new Date(startDate);
    let count = 0;
    
    while (count < maxEvents) {
      if (rule.endDate && currentDate > new Date(rule.endDate)) break;
      if (rule.count && count >= rule.count) break;
      
      // Vérifier les exceptions
      const dateStr = currentDate.toISOString().split('T')[0];
      if (rule.exceptions?.includes(dateStr)) {
        this.incrementDate(currentDate, rule.frequency, rule.interval);
        continue;
      }
      
      // Vérifier les critères spécifiques
      if (this.matchesRule(currentDate, rule)) {
        const eventDate = currentDate.toISOString().split('T')[0];
        const recurringEvent: CalendarEvent = {
          ...baseEvent,
          id: `${baseEvent.id}_recur_${count}`,
          date: eventDate
        };
        
        events.push(recurringEvent);
        count++;
      }
      
      this.incrementDate(currentDate, rule.frequency, rule.interval);
    }
    
    return events;
  }
  
  private static matchesRule(date: Date, rule: RecurrenceRule): boolean {
    if (rule.byWeekDay && rule.byWeekDay.length > 0) {
      if (!rule.byWeekDay.includes(date.getDay())) return false;
    }
    
    if (rule.byMonthDay && rule.byMonthDay.length > 0) {
      if (!rule.byMonthDay.includes(date.getDate())) return false;
    }
    
    if (rule.byMonth && rule.byMonth.length > 0) {
      if (!rule.byMonth.includes(date.getMonth() + 1)) return false;
    }
    
    return true;
  }
  
  private static incrementDate(date: Date, frequency: string, interval: number) {
    switch (frequency) {
      case 'daily':
        date.setDate(date.getDate() + interval);
        break;
      case 'weekly':
        date.setDate(date.getDate() + (7 * interval));
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + interval);
        break;
      case 'yearly':
        date.setFullYear(date.getFullYear() + interval);
        break;
    }
  }
}

// Gestionnaire de rappels
export class ReminderManager {
  private static reminders: EventReminder[] = [];
  
  static addReminder(eventId: string, type: 'popup' | 'email' | 'browser', triggerTime: number, message?: string): string {
    const reminderId = `reminder_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const event = this.getEventById(eventId);
    
    if (!event) return '';
    
    const eventDateTime = new Date(`${event.date} ${event.time || '00:00'}`);
    const scheduledTime = new Date(eventDateTime.getTime() - (triggerTime * 60 * 1000));
    
    const reminder: EventReminder = {
      id: reminderId,
      eventId,
      type,
      triggerTime,
      message: message || `Rappel: ${event.title}`,
      sent: false,
      scheduledTime
    };
    
    this.reminders.push(reminder);
    this.scheduleReminder(reminder);
    
    return reminderId;
  }
  
  private static scheduleReminder(reminder: EventReminder) {
    const now = new Date();
    const delay = reminder.scheduledTime.getTime() - now.getTime();
    
    if (delay <= 0) {
      this.triggerReminder(reminder);
      return;
    }
    
    setTimeout(() => {
      this.triggerReminder(reminder);
    }, delay);
  }
  
  private static triggerReminder(reminder: EventReminder) {
    if (reminder.sent) return;
    
    switch (reminder.type) {
      case 'popup':
        alert(reminder.message);
        break;
      case 'browser':
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Rappel Calendrier', {
            body: reminder.message,
            icon: '/favicon.ico'
          });
        }
        break;
      case 'email':
        // Ici on pourrait intégrer avec un service d'email
        console.log('Email reminder:', reminder.message);
        break;
    }
    
    reminder.sent = true;
  }
  
  private static getEventById(eventId: string): CalendarEvent | null {
    // Cette méthode devrait récupérer l'événement depuis le store/context
    // Pour l'instant, retourne null
    return null;
  }
  
  static requestNotificationPermission(): Promise<NotificationPermission> {
    if ('Notification' in window) {
      return Notification.requestPermission();
    }
    return Promise.resolve('denied');
  }
}

// Utilitaires de stockage local
export class CalendarStorage {
  private static readonly STORAGE_KEY = 'calendar_data';
  
  static saveCalendar(config: CalendarConfig): void {
    try {
      const data = {
        config,
        timestamp: Date.now(),
        version: '1.0'
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  }
  
  static loadCalendar(): CalendarConfig | null {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (!data) return null;
      
      const parsed = JSON.parse(data);
      return parsed.config;
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      return null;
    }
  }
  
  static clearCalendar(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }
}

// Utilitaires de conversion et formatage
export class CalendarFormatters {
  static formatEventDuration(event: CalendarEvent): string {
    if (event.allDay) return 'Toute la journée';
    if (!event.time) return 'Heure non spécifiée';
    
    const startTime = event.time;
    const endTime = event.endTime;
    
    if (!endTime) return startTime;
    
    const start = new Date(`2000-01-01 ${startTime}`);
    const end = new Date(`2000-01-01 ${endTime}`);
    const duration = (end.getTime() - start.getTime()) / (1000 * 60); // en minutes
    
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    
    let durationStr = '';
    if (hours > 0) durationStr += `${hours}h`;
    if (minutes > 0) durationStr += `${minutes}min`;
    
    return `${startTime} - ${endTime} (${durationStr})`;
  }
  
  static getRelativeTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Aujourd\'hui';
    if (diffDays === 1) return 'Demain';
    if (diffDays === -1) return 'Hier';
    if (diffDays > 0 && diffDays <= 7) return `Dans ${diffDays} jours`;
    if (diffDays < 0 && diffDays >= -7) return `Il y a ${Math.abs(diffDays)} jours`;
    
    return date.toLocaleDateString('fr-FR');
  }
  
  static getCategoryLabel(category: string): string {
    const labels: Record<string, string> = {
      work: '💼 Travail',
      personal: '👤 Personnel',
      meeting: '🤝 Réunion',
      reminder: '⏰ Rappel',
      holiday: '🎉 Vacances',
      other: '📋 Autre'
    };
    
    return labels[category] || '📋 Autre';
  }
  
  static getPriorityLabel(priority: string): string {
    const labels: Record<string, string> = {
      low: '🟢 Basse',
      normal: '🟡 Normale',
      high: '🔴 Haute'
    };
    
    return labels[priority] || '🟡 Normale';
  }
}

export default {
  CalendarValidator,
  CalendarAnalytics,
  CalendarExporter,
  RecurrenceManager,
  ReminderManager,
  CalendarStorage,
  CalendarFormatters
};
